import React, { useState } from "react";
import styles from "./CollapsibleList.module.scss";
import Button from "../Button/Button";

/**
 * Reusable component for displaying list of elements or child components
 * that can be collapsed and expanded.
 *
 * @param children
 * @param style
 * @returns {JSX.Element|*}
 * @constructor
 */
const CollapsibleList = ({ children, ...rest }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(
    Array.isArray(children) ? children.flat().filter(Boolean) : [children]
  );
  const [collapsedHeights] = useState([]);
  const [collapsedMaxHeight, setCollapsedMaxHeight] = useState(0);

  const getCollapsedTranslatedY = (index) => {
    const heights = collapsedHeights.slice(1, index + 1);
    return index === 0 ? 0 : heights.reduce((acc, curr) => acc + curr, 0) - index * 20;
  };

  // This will handle if there are items that are empty elements
  const handleRef = (element, index) => {
    if (element) {
      if (element.getBoundingClientRect().height === 0) {
        setItems((items) => items.filter((item) => item.key !== element.getAttribute("data-id")));
      } else {
        if (index === 0) {
          // This is due to inconsistent height of elements in the list.
          setCollapsedMaxHeight(element.getBoundingClientRect().height + 20);
        } else {
          collapsedHeights[index] = element.getBoundingClientRect().height;
        }
      }
    }
  };

  const handleClick = (index) => {
    setItems((items) => items.filter((e, idx) => idx !== index));
  };

  return (
    <div
      className={`${styles.collapsibleList} ${rest.className}`}
      data-testid="CollapsibleList"
      style={{ ...rest.style }}
    >
      {items && (
        <div className={`${styles.container} ${!open ? styles.collapsed : ""}`}>
          <div
            style={{
              maxHeight: `${
                !open && collapsedMaxHeight > 0 ? `${collapsedMaxHeight}px` : "fit-content"
              }`,
            }}
          >
            {items.map((item, index) => (
              <li
                key={index}
                data-id={item.key}
                ref={(element) => handleRef(element, index)}
                onClick={(index) => handleClick(index)}
                style={
                  !open
                    ? {
                        transform: `scaleX(${
                          1 - 0.1 * index
                        }) translateY(-${getCollapsedTranslatedY(index)}px)`,
                      }
                    : {}
                }
              >
                {item}
              </li>
            ))}
          </div>
        </div>
      )}
      {items?.length > 1 && (
        <div className="d-block text-center">
          <Button
            className={styles.collapsibleButton}
            onClick={() => {
              setOpen(!open);
              window.scrollTo(0, 0);
            }}
            label={open ? "Close All" : "View All"}
          />
        </div>
      )}
    </div>
  );
};

export default CollapsibleList;
